import {
  ChoiceInput,
  Dropdown,
  Flex,
  Input,
  Steps,
  Text,
  TextToolTip,
  Toggle,
} from '@components';
import { useUpdateUserInfoMutation } from '@features/api/userSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, InputAdornment, useTheme } from '@mui/material';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { taxDetailsFormSchema } from '@validationSchemas/investment';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import {
  ContentContainer,
  TinExcuseInput,
  ToggleBox,
} from './TaxDetails.styles';
import { TaxDetailsFormData } from './TaxDetails.types';
import countriesJSON from '@assets/countries.json';
import { useCallback, useEffect, useMemo } from 'react';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';
import { useCountriesQuery } from '@features/api/appDataSlice';
import { QuestionOutline } from '@assets/svg';
import WithUser, { WithUserProps } from '@HOCs/withUser';
import { getFlagEmoji } from '@utils/functions/getFlagFromCountryCode';
import { getPersonalDetailLockNavigation } from '../SubscriptionIntro/SubscriptionIntro.utils';
import { InvestmentContainer } from '../Investment.styles';
import { TOTAL_STEPS } from '../Investment.consts';
import { yesNoChoices } from '@utils/choiceValues/choiceValues';

const TaxDetails: React.FC<WithUserProps> = ({ user }) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();
  const { productId } = useParams();
  const { state } = useLocation();
  const { sm: isMobile } = useMediaQueries();
  const usTaxPersonBanner = document.getElementById('us-tax-person-banner');
  const [updateUserInfoMutation] = useUpdateUserInfoMutation();
  const { data: countries } = useCountriesQuery();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
    setValue,
    trigger,
    clearErrors,
  } = useForm<TaxDetailsFormData>({
    resolver: yupResolver(taxDetailsFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: {
      taxResidenceCountry: user.taxDetails?.taxResidenceCountry,
      primaryTin: user.taxDetails?.primaryTin,
      soleTaxResidence:
        !user.taxDetails?.secondaryTaxResidenceCountry &&
        !user.taxDetails?.secondaryTin,
      secondaryTaxResidenceCountry:
        user.taxDetails?.secondaryTaxResidenceCountry,
      secondaryTin: user.taxDetails?.secondaryTin,
      usTaxPayer: user.taxDetails?.usTaxPayer,
      noPrimaryIdentificationNumber: !!user?.taxDetails?.noPrimaryTinReason,
      noPrimaryTinReason: user?.taxDetails?.noPrimaryTinReason,
      noSecondaryIdentificationNumber: !!user?.taxDetails?.noSecondaryTinReason,
      noSecondaryTinReason: user?.taxDetails?.noSecondaryTinReason,
    },
  });

  const taxResidence = watch('taxResidenceCountry');
  const soleTaxResidence = watch('soleTaxResidence');
  const noPrimaryIdentificationNumber = watch('noPrimaryIdentificationNumber');
  const noSecondaryIdentificationNumber = watch(
    'noSecondaryIdentificationNumber'
  );

  useEffect(() => {
    if (noPrimaryIdentificationNumber) {
      setValue('primaryTin', '');
      trigger('primaryTin');
      clearErrors('primaryTin');
    } else {
      setValue('noPrimaryTinReason', '');
      trigger('noPrimaryTinReason');
      clearErrors('noPrimaryTinReason');
    }

    if (noSecondaryIdentificationNumber) {
      setValue('secondaryTin', '');
      trigger('secondaryTin');
      clearErrors('secondaryTin');
    } else {
      setValue('noSecondaryTinReason', '');
      trigger('noSecondaryTinReason');
      clearErrors('noSecondaryTinReason');
    }

    if (soleTaxResidence) {
      setValue('noSecondaryIdentificationNumber', false);
      setValue('noSecondaryTinReason', '');
      clearErrors(['noSecondaryIdentificationNumber', 'noSecondaryTinReason']);
    }
  }, [
    noPrimaryIdentificationNumber,
    setValue,
    clearErrors,
    trigger,
    noSecondaryIdentificationNumber,
    soleTaxResidence,
  ]);

  const countriesList = useMemo(() => {
    const countriesArray = countriesJSON as Array<any>;
    return (
      countriesArray.map((x) => ({
        name: x.name,
        value: x.name,
        icon: getFlagEmoji(x.code),
      })) ?? []
    );
  }, []);

  const allowedCountriesList = useMemo(() => {
    return (
      countries?.map((x) => ({
        name: x.name,
        value: x.name,
        icon: x.flag,
      })) ?? []
    );
  }, [countries]);

  const onSubmit = async (data: TaxDetailsFormData) => {
    try {
      await updateUserInfoMutation({
        ...user,
        taxDetails: {
          taxResidenceCountry: data.taxResidenceCountry,
          primaryTin: !data.noPrimaryIdentificationNumber
            ? data.primaryTin
            : undefined,
          secondaryTaxResidenceCountry:
            soleTaxResidence === false
              ? data.secondaryTaxResidenceCountry
              : undefined,
          secondaryTin:
            soleTaxResidence === false && !data.noSecondaryIdentificationNumber
              ? data.secondaryTin
              : undefined,
          usTaxPayer: data.usTaxPayer,
          noPrimaryTinReason: data.noPrimaryIdentificationNumber
            ? data.noPrimaryTinReason
            : undefined,
          noSecondaryTinReason: data.noSecondaryIdentificationNumber
            ? data.noSecondaryTinReason
            : undefined,
        },
      }).unwrap();

      handleNextButtonNavigation(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleNextButtonNavigation = (data: TaxDetailsFormData): void => {
    if (data.usTaxPayer) {
      navigate(`/investment-subscription/${productId}/unavailable`, {
        preventScrollReset: false,
        state: {
          progress: 4,
          type: 'us_tax_person',
          previousPathName: 'tax-details',
        },
        replace: true,
      });
    } else if (state?.isEdit) {
      navigate(
        `/investment-subscription/${productId}/investment-profile-info`,
        {
          state: {
            progress: 5,
          },
          replace: true,
        }
      );
    } else {
      navigate(`/investment-subscription/${productId}/bank-details`, {
        preventScrollReset: false,
        state: {
          progress: 5,
        },
        replace: true,
      });
    }
  };

  const handlePreviousButtonClick = useCallback(() => {
    navigate(`/investment-subscription/${productId}/contact-details`, {
      state: {
        progress: 3,
      },
      replace: true,
    });
  }, [navigate, productId]);

  if (user.personalDetailsLocked) {
    getPersonalDetailLockNavigation(navigate, productId)();
  }
  return (
    <InvestmentContainer data-testid='tax-details-container'>
      <ContentContainer>
        <Steps currentStep={5} totalSteps={TOTAL_STEPS} />
        <Text
          variant='h3'
          fontWeight='fontWeightBold'
          sx={{ mt: { xs: 16, md: 5 } }}
          color={theme.palette.black.A100}
        >
          <FormattedMessage id='investment.taxDetails.taxDetails' />
        </Text>
        <Box mt={16}>
          <Controller
            control={control}
            name='taxResidenceCountry'
            render={({ field }) => (
              <Dropdown
                label={intl.formatMessage({
                  id: 'investment.taxDetails.taxResidence',
                })}
                inputProps={{ 'data-testid': 'tax-residence' }}
                menuItemData={allowedCountriesList}
                {...field}
              />
            )}
          />
        </Box>
        <Input
          label={intl.formatMessage({
            id: 'investment.taxDetails.taxpayerIdentificationNumber',
          })}
          fullWidth
          sx={{ mt: 16 }}
          disabled={!!noPrimaryIdentificationNumber}
          inputProps={{ 'data-testid': 'taxpayer-identification-number' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <TextToolTip
                  text=''
                  toolTipText={intl.formatMessage(
                    {
                      id: 'investment.taxDetails.tinTooltipMessage',
                    },
                    {
                      br: <br />,
                    }
                  )}
                  icon={() => (
                    <QuestionOutline color={theme.palette.grey.A100} />
                  )}
                />
              </InputAdornment>
            ),
          }}
          {...registerInput(register, 'primaryTin')}
          {...getErrorProps(intl, errors.primaryTin)}
        />
        <ToggleBox>
          <Controller
            control={control}
            name='noPrimaryIdentificationNumber'
            render={({ field }) => (
              <Toggle
                label={intl.formatMessage({
                  id: 'investment.taxDetails.notHaveTaxpayerIdentificationNumber',
                })}
                labelPlacement='end'
                labelProps={{
                  variant: 'h6',
                  fontWeight: 'fontWeightMedium',
                }}
                {...field}
                checked={field.value}
              />
            )}
          />
        </ToggleBox>
        <TinExcuseInput
          label={intl.formatMessage({
            id: 'investment.taxDetails.reasonForNonAvailability',
          })}
          fullWidth
          sx={{
            my: 8,
            display: noPrimaryIdentificationNumber ? 'initial' : 'none',
          }}
          multiline
          inputProps={{
            'data-testid': 'primary-tin-excuse',
            sx: { pl: 5, pt: { xs: 8, lg: 3 } },
          }}
          {...registerInput(register, 'noPrimaryTinReason')}
          {...getErrorProps(intl, errors.noPrimaryTinReason)}
        />
        <Flex mt={8} mb={5}>
          <TextToolTip
            text={intl.formatMessage(
              {
                id: 'investment.taxDetails.taxResidencyMessage',
              },
              {
                taxResidence:
                  taxResidence ??
                  intl.formatMessage({
                    id: 'investment.taxDetails.residenceProvided',
                  }),
              }
            )}
            toolTipText={intl.formatMessage({
              id: 'investment.taxDetails.taxResidencyTooltipMessage',
            })}
          />
        </Flex>
        <Controller
          control={control}
          name='soleTaxResidence'
          render={({ field: { ref, value, ...restProps } }) => (
            <ChoiceInput
              direction={isMobile ? 'column' : 'row'}
              fullWidth
              choices={yesNoChoices}
              defaultValues={value !== undefined ? [value] : undefined}
              {...restProps}
            />
          )}
        />
        <Box
          mt={16}
          flexDirection='column'
          display={soleTaxResidence === false ? 'flex' : 'none'}
        >
          <Controller
            control={control}
            name='secondaryTaxResidenceCountry'
            render={({ field }) => (
              <Dropdown
                label={intl.formatMessage({
                  id: 'investment.taxDetails.secondTaxResidence',
                })}
                inputProps={{ 'data-testid': 'tax-residence-secondary' }}
                menuItemData={countriesList}
                {...field}
              />
            )}
          />
          <Input
            label={intl.formatMessage({
              id: 'investment.taxDetails.taxpayerIdentificationNumberSecondary',
            })}
            fullWidth
            sx={{ mt: 16 }}
            disabled={!!noSecondaryIdentificationNumber}
            inputProps={{
              'data-testid': 'taxpayer-identification-number-secondary',
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <TextToolTip
                    text=''
                    toolTipText={intl.formatMessage(
                      {
                        id: 'investment.taxDetails.tinTooltipMessage',
                      },
                      {
                        br: <br />,
                      }
                    )}
                    icon={() => (
                      <QuestionOutline color={theme.palette.grey.A100} />
                    )}
                  />
                </InputAdornment>
              ),
            }}
            {...registerInput(register, 'secondaryTin')}
            {...getErrorProps(intl, errors.secondaryTin)}
          />
          <ToggleBox>
            <Controller
              control={control}
              name='noSecondaryIdentificationNumber'
              render={({ field }) => (
                <Toggle
                  label={intl.formatMessage({
                    id: 'investment.taxDetails.notHaveTaxpayerIdentificationNumber',
                  })}
                  labelPlacement='end'
                  labelProps={{
                    variant: 'h6',
                    fontWeight: 'fontWeightMedium',
                  }}
                  {...field}
                  checked={field.value}
                />
              )}
            />
          </ToggleBox>
          <TinExcuseInput
            label={intl.formatMessage({
              id: 'investment.taxDetails.reasonForNonAvailability',
            })}
            fullWidth
            sx={{
              my: 8,
              display: noSecondaryIdentificationNumber ? 'initial' : 'none',
            }}
            multiline
            inputProps={{
              'data-testid': 'secondary-tin-excuse',
              sx: { pl: 5, pt: { xs: 8, lg: 3 } },
            }}
            {...registerInput(register, 'noSecondaryTinReason')}
            {...getErrorProps(intl, errors.noSecondaryTinReason)}
          />
        </Box>
        <Flex mt={15} mb={5}>
          <TextToolTip
            text={intl.formatMessage({
              id: 'investment.taxDetails.usTaxPurposes',
            })}
            toolTipText={intl.formatMessage({
              id: 'investment.taxDetails.usPersonTooltipMessage',
            })}
          />
        </Flex>
        <Controller
          control={control}
          name='usTaxPayer'
          render={({ field: { ref, value, ...restProps } }) => (
            <ChoiceInput
              direction={isMobile ? 'column' : 'row'}
              fullWidth
              choices={yesNoChoices}
              defaultValues={value !== undefined ? [value] : undefined}
              {...restProps}
              onChange={async (currentValue) => {
                await restProps.onChange(currentValue);
                if (currentValue && usTaxPersonBanner !== null) {
                  usTaxPersonBanner.scrollIntoView({
                    behavior: 'smooth',
                    block: currentValue ? 'start' : 'end',
                  });
                }
              }}
            />
          )}
        />
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: handlePreviousButtonClick,
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !isValid,
          onClick: handleSubmit(onSubmit),
        }}
      />
    </InvestmentContainer>
  );
};

export default WithUser(TaxDetails);
